import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { SIGNAL_META, SIGNAL_PRIORITY, FILTER_TO_STAGE, MOVEMENT, evidenceInfo, conceptName } from "@/lib/breakoutLabels";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "watch", label: "Watch" },
  { key: "interesting", label: "Interesting" },
  { key: "strong", label: "Strong" },
  { key: "investigate", label: "Investigate" },
  { key: "cross-source", label: "Cross-Source" },
];

function SignalChip({ stage }) {
  const m = SIGNAL_META[stage] || SIGNAL_META.HOLDING_STEADY;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-mono px-2 py-0.5 border ${m.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} /> {m.label}
    </span>
  );
}

function whatsHappening(c) {
  const mv = c.movement || [];
  const labels = mv.map((m) => MOVEMENT[m]?.label).filter(Boolean);
  return labels.length ? labels.join(" · ") : "Holding steady";
}

export default function Products() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [concepts, setConcepts] = useState(null);
  const signal = params.get("signal") || "all";

  useEffect(() => {
    api.get("/amazon/trajectory-board").then((r) => setConcepts(r.data.concepts || []));
  }, []);

  const rows = useMemo(() => {
    if (!concepts) return [];
    let list = concepts;
    if (signal === "new") {
      list = concepts.filter((c) => (c.movement || []).includes("NEW_ASINS_APPEARING"));
    } else if (signal !== "all") {
      const stage = FILTER_TO_STAGE[signal];
      list = concepts.filter((c) => c.current_signal?.stage === stage);
    }
    return [...list].sort((a, b) => {
      const pa = SIGNAL_PRIORITY[a.current_signal?.stage] ?? 9;
      const pb = SIGNAL_PRIORITY[b.current_signal?.stage] ?? 9;
      if (pa !== pb) return pa - pb;
      return (b.observation_count || 0) - (a.observation_count || 0);
    });
  }, [concepts, signal]);

  const setFilter = (k) => setParams(k === "all" ? {} : { signal: k });

  return (
    <div className="p-8 max-w-[1400px]" data-testid="products-candidate-list">
      <h1 className="font-head text-2xl font-bold tracking-tight mb-1">Product ideas</h1>
      <p className="text-[#71717A] text-sm mb-4">Live candidates from Amazon US New Releases — tracked by trajectory, not yet fully scored.</p>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex border border-[#27272A]">
          {FILTERS.map((x) => (
            <button
              key={x.key}
              data-testid={`filter-${x.key}`}
              onClick={() => setFilter(x.key)}
              className={`px-3 py-1.5 text-xs font-mono border-r border-[#27272A] last:border-r-0 transition-colors ${
                signal === x.key ? "bg-[#F4F4F5] text-[#09090B]" : "text-[#A1A1AA] hover:bg-[#18181B]"
              }`}
            >
              {x.label}
            </button>
          ))}
        </div>
        <span className="text-xs font-mono text-[#71717A]">{rows.length} shown</span>
      </div>

      <div className="border border-[#27272A] overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead>
            <tr className="text-[11px] uppercase tracking-widest text-[#71717A] font-mono border-b border-[#27272A]">
              <th className="text-left px-4 py-3">Product</th>
              <th className="text-left px-4 py-3">Current Signal</th>
              <th className="text-left px-4 py-3">What's happening</th>
              <th className="text-right px-4 py-3">Days</th>
              <th className="text-right px-4 py-3">Listings</th>
              <th className="text-left px-4 py-3">Evidence</th>
              <th className="text-left px-4 py-3">Next action</th>
            </tr>
          </thead>
          <tbody>
            {concepts === null ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-[#71717A] font-mono text-xs">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-[#71717A] font-mono text-xs">No product ideas in this view.</td></tr>
            ) : rows.map((c) => {
              const ev = evidenceInfo(c.evidence_grade);
              return (
                <tr
                  key={c.canonical_concept}
                  data-testid={`candidate-row-${c.canonical_concept}`}
                  onClick={() => nav("/trajectory")}
                  className="border-b border-[#1c1c1f] hover:bg-[#141416] cursor-pointer"
                >
                  <td className="px-4 py-3 font-head font-medium">{conceptName(c.canonical_concept)}</td>
                  <td className="px-4 py-3"><SignalChip stage={c.current_signal?.stage} /></td>
                  <td className="px-4 py-3 text-[#A1A1AA] text-xs">{whatsHappening(c)}</td>
                  <td className="px-4 py-3 text-right font-mono text-xs">{c.span_calendar_days ?? 0}</td>
                  <td className="px-4 py-3 text-right font-mono text-xs">{c.unique_asins ?? 0}</td>
                  <td className="px-4 py-3 text-xs"><span className="text-[#A1A1AA]">{ev.label}</span>
                    {ev.detail && <span className="text-[#71717A]"> — {ev.detail}</span>}</td>
                  <td className="px-4 py-3 text-[11px] font-mono text-emerald-400">{c.current_signal?.next_action || "Keep watching"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-[#52525B] font-mono mt-3">
        Composite / Verdict / Confidence are not shown — these candidates have not been through the full V1 scoring pipeline (Deep Validation).
      </p>
    </div>
  );
}
